import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Pause, Play, X, ChevronRight, MessageCircle, CheckCircle2,
} from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import AskPeterModal from '../components/AskPeterModal';
import TryVideraModal from '../components/TryVideraModal';

/**
 * Videra — 2-minute self-guided tour.
 * Step-by-step click-through using real product screenshots + one-line captions.
 * ~18 seconds per step * 7 steps = ~2 minutes.
 * Auto-advances (paused on hover), keyboard navigable, and pre-loads the next shot.
 */
const STEPS = [
  {
    kicker: 'The portfolio',
    title: 'Every scheme, every stage, at a glance.',
    body: 'Live capital, minor works, lifecycle and estates rollup, with the numbers your board actually asks for.',
    image: '/images/nhs/nhs-portfolio-tracker.jpg',
  },
  {
    kicker: 'PMO command centre',
    title: 'The room your PMO wishes it lived in.',
    body: 'Every Trust, every project, every stage, one place, one truth. No spreadsheet reconciliation.',
    image: '/images/nhs/nhs-pmo-command-centre.jpg',
  },
  {
    kicker: 'Project stages',
    title: 'RIBA-aligned gates with evidence attached.',
    body: 'Every stage. Every approval. The audit trail writes itself as the work happens.',
    image: '/images/healthcare/hc-project-stages.jpg',
  },
  {
    kicker: 'Handback readiness',
    title: 'Progressive handback, not a last-minute panic.',
    body: 'Track readiness for PFI-adjacent contracts from day one, not day 2,556.',
    image: '/images/healthcare/hc-handback-readiness.jpg',
  },
  {
    kicker: 'Governance reports',
    title: 'Board packs composed from live work.',
    body: 'No more Sunday-night PowerPoint marathons. The pack assembles from the day\u2019s evidence.',
    image: '/images/nhs/nhs-governance-reports.jpg',
  },
  {
    kicker: 'Compliance rails',
    title: 'HTM, SHTM, IPC and statutory duty in one view.',
    body: 'The obligations you owe, the evidence you have, the gaps you need to close.',
    image: '/images/nhs/nhs-compliance-rails.jpg',
  },
  {
    kicker: 'Ready to try it live?',
    title: '14 days on Specialist. 7 days on PM. Both fully unlocked.',
    body: 'Every feature, every workflow, every screen. Load your world, invite your team, prove it works on your real project.',
    image: '/images/nhs/nhs-board-pack.jpg',
    isFinal: true,
  },
];

const STEP_DURATION_MS = 18000;

const V2Tour = () => {
  useSEO({
    title: 'Take the 2-minute Videra tour',
    description: 'A quick self-guided walkthrough of the Videra platform — portfolio, PMO, gates, handback readiness, governance and compliance. No demo call needed.',
    path: '/tour',
  });

  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [askPeterOpen, setAskPeterOpen] = useState(false);
  const [tryOpen, setTryOpen] = useState(false);
  const startRef = useRef(0);
  const rafRef = useRef(null);
  const [progress, setProgress] = useState(0);

  const current = STEPS[index];
  const isFinal = current.isFinal;

  // Preload next image
  useEffect(() => {
    const next = STEPS[index + 1];
    if (next) {
      const img = new Image();
      img.src = next.image;
    }
  }, [index]);

  // Auto-advance timer with progress bar
  useEffect(() => {
    if (!playing) return;
    if (isFinal) return; // stop on final step
    startRef.current = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - startRef.current) / STEP_DURATION_MS);
      setProgress(t);
      if (t >= 1) {
        setIndex((i) => (i + 1 < STEPS.length ? i + 1 : i));
        setProgress(0);
      } else {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [index, playing, isFinal]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') setIndex((i) => Math.min(STEPS.length - 1, i + 1));
      if (e.key === 'ArrowLeft') setIndex((i) => Math.max(0, i - 1));
      if (e.key === ' ') { e.preventDefault(); setPlaying((p) => !p); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const next = () => { setIndex((i) => Math.min(STEPS.length - 1, i + 1)); setProgress(0); };
  const prev = () => { setIndex((i) => Math.max(0, i - 1)); setProgress(0); };
  const jump = (i) => { setIndex(i); setProgress(0); };

  const totalDurationSec = useMemo(() => Math.round((STEPS.length * STEP_DURATION_MS) / 1000), []);

  return (
    <div data-testid="v2-tour" className="min-h-[calc(100vh-4rem)] bg-slate-950 text-white">
      <AskPeterModal open={askPeterOpen} onClose={() => setAskPeterOpen(false)} />
      <TryVideraModal open={tryOpen} onClose={() => setTryOpen(false)} />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
        {/* Header row */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-blue-400 font-semibold">Self-guided tour</p>
            <h1 className="text-xl md:text-2xl font-medium tracking-tight mt-1">
              Videra in {Math.round(totalDurationSec / 60)} minutes.
            </h1>
          </div>
          <Link
            to="/"
            data-testid="v2-tour-close"
            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            aria-label="Close tour"
          >
            <X className="w-4 h-4" />
          </Link>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-1.5 mb-4" data-testid="v2-tour-steps">
          {STEPS.map((s, i) => (
            <button
              key={i}
              onClick={() => jump(i)}
              data-testid={`v2-tour-step-${i}`}
              className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden group hover:bg-white/20 transition-colors"
              aria-label={`Go to step ${i + 1}`}
            >
              <div
                className="h-full bg-blue-400 transition-all"
                style={{
                  width: i < index ? '100%' : i === index ? `${progress * 100}%` : '0%',
                }}
              />
            </button>
          ))}
        </div>

        {/* Main screenshot frame */}
        <div className="relative bg-slate-900 rounded-2xl p-2 shadow-[0_30px_80px_-25px_rgba(0,0,0,0.6)] ring-1 ring-white/5" data-testid="v2-tour-screen">
          <img
            src={current.image}
            alt={current.title}
            key={current.image}
            className="w-full aspect-[16/9] object-cover object-top rounded-lg animate-[videra-tour-in_.5s_ease-out]"
            loading="eager"
          />
        </div>

        {/* Caption + controls */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 items-start">
          <div className="animate-[videra-tour-in_.4s_.1s_ease-out_both]" key={index}>
            <p className="text-[11px] uppercase tracking-[0.22em] text-blue-400 font-semibold mb-2">{current.kicker}</p>
            <h2 className="text-2xl md:text-3xl font-medium tracking-tight leading-tight">{current.title}</h2>
            <p className="mt-3 text-slate-300 leading-relaxed max-w-2xl">{current.body}</p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={prev}
              disabled={index === 0}
              data-testid="v2-tour-prev"
              className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
              aria-label="Previous step"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPlaying((p) => !p)}
              data-testid="v2-tour-playpause"
              className="w-11 h-11 rounded-full bg-white text-slate-900 hover:bg-slate-100 flex items-center justify-center transition-colors"
              aria-label={playing ? 'Pause' : 'Play'}
            >
              {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" fill="currentColor" />}
            </button>
            <button
              onClick={next}
              disabled={index === STEPS.length - 1}
              data-testid="v2-tour-next"
              className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
              aria-label="Next step"
            >
              <ArrowRight className="w-4 h-4" />
            </button>
            <span className="ml-2 text-xs text-slate-400 font-mono tabular-nums whitespace-nowrap">
              {String(index + 1).padStart(2, '0')} / {String(STEPS.length).padStart(2, '0')}
            </span>
          </div>
        </div>

        {/* Final-step CTAs */}
        {isFinal && (
          <div className="mt-8 p-6 md:p-8 rounded-2xl bg-gradient-to-br from-blue-950 to-slate-900 ring-1 ring-blue-500/20 animate-[videra-tour-in_.5s_.2s_ease-out_both]" data-testid="v2-tour-final">
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setTryOpen(true)}
                data-testid="v2-tour-cta-try"
                className="inline-flex items-center gap-2 bg-white text-slate-900 px-6 py-3.5 rounded-md text-sm font-medium hover:bg-slate-100 transition-colors group"
              >
                Try Videra free
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
              <button
                type="button"
                onClick={() => setAskPeterOpen(true)}
                data-testid="v2-tour-cta-ask"
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3.5 rounded-md text-sm font-medium hover:bg-blue-500 transition-colors"
              >
                <MessageCircle className="w-4 h-4" /> Ask Peter, 20 min
              </button>
              <Link
                to="/clients"
                className="inline-flex items-center gap-1.5 text-slate-300 hover:text-white text-sm font-medium underline-offset-4 hover:underline"
              >
                Read the VINCI story <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-400">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> No credit card</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Every feature unlocked</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Invite your whole team</span>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes videra-tour-in {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default V2Tour;
